import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  DownloadSimpleIcon,
  EnvelopeIcon,
  FlagIcon,
  HighlighterIcon,
  ListChecksIcon,
  ProhibitIcon,
  SpinnerIcon,
  WarningCircleIcon,
  WarningIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { useToast } from 'src/context/ToastContext';
import { useArCustomerContacts } from '@/api/rpc/hooks/accounts-receivable/useArCustomerContacts';
import { useArStatementDefaults } from '@/api/rpc/hooks/accounts-receivable/useArStatementDefaults';
import { useArStatementEmail } from '@/api/rpc/hooks/accounts-receivable/useArStatementEmail';
import { useRefreshArInvoiceHeadersFromFamous } from '@/api/rpc/hooks/accounts-receivable/useRefreshArInvoiceHeadersFromFamous';
import { useRefreshArInvoiceHeadersForCustomersFromFamous } from '@/api/rpc/hooks/accounts-receivable/useRefreshArInvoiceHeadersForCustomersFromFamous';
import { useGetModuleConfig } from '@/api/org/config/modules';
import {
  AR_DEFAULT_STATEMENT_RECIPIENT_ROLES,
  ProductModule,
} from '@erp/shared-types';
import type { ArStatementRecipientRolesMode } from '@erp/shared-types';
import { AR_STATEMENT_TELEMETRY } from '@erp/shared-utils';
import useAuth from '@/api/auth/useAuth';
import { useFlowTelemetry } from '@/hooks/useFlowTelemetry';
import { orpc } from '@/api/rpc/client';
import { ArReportSettings } from '@/components/accounts-receivable/ArReportSettings';
import {
  type ArReportFilters,
  DEFAULT_AR_REPORT_FILTERS,
  applyBasisLock,
  arAgingPayload,
} from '@/components/accounts-receivable/ar-report-filters';
import { useArStatementBasisLock } from '@/api/rpc/hooks/accounts-receivable/useArStatementBasisLock';
import { HtmlWYSIWYGEditor } from '@/components/common/HtmlWYSIWYGEditor';
import { EmailRecipientsPanel } from '@/components/common/email-composer/EmailRecipientsPanel';
import {
  OutboundEmailViewerBody,
  useOutboundEmailViewData,
} from '@/components/communications/outbound-email-viewer-shared';
import {
  EMAIL_REGEX,
  isSendableEmail,
  makeRecipientId,
  type CustomRecipient,
  type RecipientRole,
} from '@/components/common/email-composer/types';
import { templateVariableMarkersToMustache } from '@/components/common/lexical-template-variables/TemplateVariableNode';
import { differenceInDays, format as formatDateFn } from 'date-fns';
import { Plural, T, Var, useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import { formatDecimalCurrency, formatDate } from '../ar-format';
import { parseDateOnly } from '@/utils/dateUtils';
import { downloadCSV } from '@/utils/csvDownload';
import {
  buildBulkStatementSummaryRecords,
  buildBulkStatementSummaryTsv,
} from './bulkStatementSummaryReport';
import {
  assignStatementRecipientRoles,
  buildStatementRecipientPayload,
  splitStatementRecipients,
  type StatementRecipientSplit,
} from './statementRecipientRoles';
import type {
  ArInvoiceListItem,
  ArStatementSendSummary,
  EmailImportance,
  InvoiceHighlightColor,
  SendFromOverride,
} from '@erp/rpc';

// ─── Types ───────────────────────────────────────────────────────────────────

interface SendStatementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customers: Array<{
    customerId: string;
    customerName: string | null;
    openBalance?: string | null;
    lastStatementSentAt?: string | null;
  }>;
  reportSettings?: ArReportFilters;
  /**
   * Per-caller override for how the customer's "Customer Statement"-tagged
   * contacts are split across To/Cc, winning over the org's AR setting. Set by
   * the payment-reminder task when its workflow step configured its own mode,
   * so one reminder run can address statements differently from the org
   * default. Omitted by every other entry point, which stays on the org
   * setting.
   */
  statementRecipientRolesOverride?: ArStatementRecipientRolesMode;
  /**
   * When true, "Send All" first fires a customer-scoped bulk header refresh
   * against Famous for every pending customer (server resolves their open
   * invoices and refreshes them, chunked), so statements sent to customers the
   * user never individually reviewed still reflect the latest source-of-truth.
   * Best-effort — a Famous/Oracle problem falls back to already-ingested data.
   * Per-customer review doesn't need this: each viewed customer is refreshed by
   * the post-preview path as it's opened.
   */
  refreshBeforeSendAll?: boolean;
  /**
   * Called each time a statement is successfully sent to a customer (so it may
   * fire more than once across a bulk send). Callers should treat it
   * idempotently — e.g. the AR task dashboard uses it to mark a queued
   * "send statements" task complete once the dialog closes.
   */
  onStatementSent?: () => void;
  /**
   * Optional one-line explanation of how `customers` was arrived at, shown above
   * the review surface. Used by the payment-reminder task, whose list is
   * re-resolved on open: it reports how many customers dropped out because they
   * already received a statement (or that the refresh failed), so a shorter list
   * than the task title promises isn't mistaken for missing work.
   */
  notice?: string;
}

interface SendResult {
  status: 'sent' | 'skipped' | 'failed' | 'canceled';
  customerName: string | null;
  error?: string;
  // Set only for 'sent' results — the persisted outbound email, so the summary
  // can open it in the sent-email viewer to confirm what actually went out.
  outboundEmailId?: string;
  // Set only for 'sent' results — the server-derived report row (ERP display
  // name + oldest invoice on the statement) that feeds the summary's
  // download/copy actions. Server-derived so it covers customers the user
  // never opened during a Send All.
  summary?: ArStatementSendSummary;
}

// Snapshot of all per-customer editable state. Captured when navigating away
// from a customer so back/forward navigation restores the user's edits and
// they can build up drafts across the whole list before sending.
interface CustomerDraft {
  selectedEmails: Map<string, RecipientRole>;
  customRecipients: CustomRecipient[];
  excludedInvoiceIds: Set<string>;
  subjectDraft: string;
  bodyDraft: string;
  pdfFilenameDraft: string;
  invoiceHighlights: Map<string, InvoiceHighlightColor>;
  bccSender: boolean;
  selectedSendFromKey: string;
  // The customer's full (pre-exclusion) invoice list, so re-entering them
  // renders the table straight away instead of waiting on — and depending on
  // the timing of — a refetch. A preview that carries excludeInvoiceIds comes
  // back already filtered and so can never rebuild this list on its own.
  allInvoices: ArInvoiceListItem[];
  // Platform-generated baselines, captured when the drafts are first seeded
  // from the preview. Used only to tell whether the user edited the subject or
  // body before sending (the `editedBody` telemetry flag). The body baseline is
  // stored in mustache form so auto-updated template-variable values don't read
  // as edits.
  seededSubject: string;
  baselineBodyMustache: string;
}

/**
 * Statement-specific augmentation of the shared SendFromOption: carries the
 * resolved SendFromOverride payload so the dialog can look up the selected
 * outbox by `key` at send time. The shared panel only needs `{key,label}`.
 */
interface StatementSendFromOption {
  /**
   * Stable identifier for the dropdown selection. `configured` for the AR
   * module config alias; `user:<email>` for a user mailbox. Kept distinct
   * from the underlying SendFromOverride payload so the <Select> value stays
   * a primitive.
   */
  key: string;
  label: string;
  sendFrom: SendFromOverride;
}

const PLACEHOLDER_EMAIL = 'preview@placeholder.local';

const DEFAULT_REPORT_SETTINGS: ArReportFilters = DEFAULT_AR_REPORT_FILTERS;

// Identifies which invoice set a preview represents, so we can tell whether the
// currently-shown preview is fresh for the customer/config the send would use.
// Keyed on exactly the inputs that determine the server-resolved invoice set:
// customer, aging payload, and manual exclusions (highlights/subject/body don't
// change which invoices are sent, so they're excluded). `displayPreview` is
// retained across customer navigation and config-change refetches to keep the
// PDF mounted, so identity alone can't distinguish current from stale.
function buildPreviewContentKey(
  customerId: string | undefined,
  reportConfig: ArReportFilters,
  excludedInvoiceIds: Set<string>,
): string {
  return JSON.stringify({
    customerId: customerId ?? null,
    aging: arAgingPayload(reportConfig),
    excluded: Array.from(excludedInvoiceIds).sort(),
  });
}

// Click-to-cycle highlight palette. Hex values match HIGHLIGHT_FILL in
// ArAgingReportPDFService so the on-screen swatch matches the PDF row fill.
const HIGHLIGHT_CYCLE: (InvoiceHighlightColor | null)[] = [
  null,
  'yellow',
  'red',
  'green',
];

const HIGHLIGHT_BG: Record<InvoiceHighlightColor, string> = {
  yellow: '#FEF3C7',
  red: '#FEE2E2',
  green: '#D1FAE5',
};

// ─── Component ───────────────────────────────────────────────────────────────

export function SendStatementDialog({
  open,
  onOpenChange,
  customers: customersProp,
  reportSettings = DEFAULT_REPORT_SETTINGS,
  statementRecipientRolesOverride,
  refreshBeforeSendAll,
  onStatementSent,
  notice,
}: SendStatementDialogProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { track: trackStatement } = useFlowTelemetry(
    AR_STATEMENT_TELEMETRY.module,
    AR_STATEMENT_TELEMETRY.flow,
  );
  const statementSteps = AR_STATEMENT_TELEMETRY.steps;

  // Per-session telemetry guards. subject_edited / body_edited fire at most once
  // per customer (their handlers run on every keystroke / editor re-render).
  // sessionEngagedRef remembers whether the user customized anything and
  // sessionSentRef whether any statement was sent, so closing the dialog can be
  // reported as either a completed or an (optionally engaged) abandoned journey.
  const subjectEditedCustomers = useRef<Set<string>>(new Set());
  const bodyEditedCustomers = useRef<Set<string>>(new Set());
  const sessionEngagedRef = useRef(false);
  const sessionSentRef = useRef(false);

  // Present customers in a stable alphabetical order by name. The queued list
  // from the payment-reminder scan (and other callers) arrives unordered, and a
  // predictable order makes the step-through rail easier to scan. Every
  // index-based navigation and per-customer lookup below reads from this sorted
  // array, so ordering stays consistent throughout the dialog.
  const customers = useMemo(
    () =>
      [...customersProp].sort((a, b) =>
        (a.customerName ?? '').localeCompare(b.customerName ?? '', undefined, {
          sensitivity: 'base',
        }),
      ),
    [customersProp],
  );

  const isSingle = customers.length === 1;

  // Step-through state
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<'review' | 'summary'>('review');
  const [results, setResults] = useState<Map<string, SendResult>>(new Map());
  // Per-customer drafts captured on every navigation away from a customer,
  // so back/forward through the list keeps the user's edits intact. Cleared
  // only when the dialog closes.
  const [customerDrafts, setCustomerDrafts] = useState<
    Map<string, CustomerDraft>
  >(new Map());

  // Report config (synced from parent on open, editable within dialog).
  // The whole settings object is one piece of state so adding a new filter
  // field doesn't require updating this dance.
  const [reportConfig, setReportConfig] =
    useState<ArReportFilters>(reportSettings);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setReportConfig(reportSettings);
      subjectEditedCustomers.current.clear();
      bodyEditedCustomers.current.clear();
      bodyBaselineByCustomer.current.clear();
      // Clear alongside bodyBaselineByCustomer — the two track the same per-
      // customer baseline state and must move together. Leaving this set stale
      // across a reopen skips baseline recapture (see handleBodyChange), so
      // baselineBodyMustacheRef keeps a prior session's value and send-time
      // computeEditedBody reports a false `editedBody`. Reset the single-slot
      // refs too so nothing carries over from the last session's customer.
      bodyBaselineCapturedCustomers.current.clear();
      seededSubjectRef.current = '';
      baselineBodyMustacheRef.current = '';
      sessionEngagedRef.current = false;
      sessionSentRef.current = false;
      trackStatement(statementSteps.dialogOpened, {
        customerCount: customers.length,
        mode: isSingle ? 'single' : 'bulk',
      });
    }
    wasOpen.current = open;
  }, [
    open,
    reportSettings,
    customers.length,
    isSingle,
    trackStatement,
    statementSteps,
  ]);

  // Org-level basis lock: when enabled, the basis selector is hidden and the
  // configured basis is forced into every preview/send payload. Derived (not
  // stored) so it always reflects the latest config even if it loads after the
  // dialog opens, and so a hidden selector can never drift out of sync.
  const basisLock = useArStatementBasisLock();
  const effectiveReportConfig = useMemo(
    () => applyBasisLock(reportConfig, basisLock),
    [reportConfig, basisLock],
  );

  // Per-customer transient state
  // Maps email → recipient role for explicit assignment.
  const [selectedEmails, setSelectedEmails] = useState<
    Map<string, RecipientRole>
  >(new Map());
  const [customRecipients, setCustomRecipients] = useState<CustomRecipient[]>(
    [],
  );
  // Which recipient row is currently showing its inline "add email" input.
  // Transient inline-add state (which row's "+ Add" is open, what's typed,
  // whether last commit failed) is owned by EmailRecipientsPanel now; the
  // panel is keyed on customerId below so it resets per customer.
  const [excludedInvoiceIds, setExcludedInvoiceIds] = useState<Set<string>>(
    new Set(),
  );
  const [previewError, setPreviewError] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [previewTab, setPreviewTab] = useState('invoices');

  // Per-customer email content drafts. Empty string = "use template default".
  // Seeded from previewData on first available preview per customer (see effect
  // below). Sent as overrides on `send`; NOT included in `preview` calls so
  // changes to report config (minDays, basis) keep producing fresh template
  // renders for the PDF and invoice list.
  const [subjectDraft, setSubjectDraft] = useState('');
  const [bodyDraft, setBodyDraft] = useState('');
  const [pdfFilenameDraft, setPdfFilenameDraft] = useState('');

  // Platform-generated baselines for the current customer, used only to compute
  // the `editedBody` telemetry flag (see computeEditedBody). The body baseline
  // is the editor's first emitted value (markers form) converted to mustache,
  // captured per customer in handleBodyChange.
  const seededSubjectRef = useRef('');
  const baselineBodyMustacheRef = useRef('');
  const bodyBaselineCapturedCustomers = useRef<Set<string>>(new Set());
  // Per-customer body baselines for the body_edited telemetry compare. Kept
  // separate from the single baselineBodyMustacheRef above (which the send-time
  // editedBody logic reads) because that ref holds only the *most recent*
  // customer's baseline — navigating A→B→A would compare A against B's baseline
  // and fire a false body_edited. Keyed by customerId, this never cross-
  // contaminates. Cleared per dialog session in the open effect below.
  const bodyBaselineByCustomer = useRef<Map<string, string>>(new Map());

  // Per-row PDF highlights, keyed by invoiceId. Sent on both preview and send
  // so the PDF tab reflects color changes live.
  const [invoiceHighlights, setInvoiceHighlights] = useState<
    Map<string, InvoiceHighlightColor>
  >(new Map());

  // Per-send "send from" routing. Empty string = no option selected yet
  // (e.g. before sendFromOptions has been populated). Otherwise this is the
  // `key` of a SendFromOption, which is mapped to its discriminated SendFrom
  // payload when calling preview/send.
  const [selectedSendFromKey, setSelectedSendFromKey] = useState('');
  // "BCC me" (copy the sending user) defaults off — statements BCC the AR-config
  // outboxBccEmails for a record copy instead. The user can still opt in.
  const [bccSender, setBccSender] = useState(false);
  // Priority flag applied to every statement sent from this dialog session.
  // Dialog-level (not per-customer) so a "mark this batch urgent" choice
  // persists as the clerk pages through customers.
  const [importance, setImportance] = useState<EmailImportance>('normal');

  const { data: arModuleConfig, isPending: arModuleConfigPending } =
    useGetModuleConfig(ProductModule.AR_INVOICING);
  const { currentUser } = useAuth();

  const sendFromOptions = useMemo<StatementSendFromOption[]>(() => {
    const options: StatementSendFromOption[] = [];
    const arConfig = arModuleConfig?.config?.ar;
    if (arConfig?.outboxMailboxUpn) {
      options.push({
        key: 'configured',
        label: arConfig.outboxSenderAddress ?? arConfig.outboxMailboxUpn,
        sendFrom: { mode: 'configured' },
      });
    }
    const userEmail = currentUser?.emails[0];
    if (userEmail) {
      options.push({
        key: `user:${userEmail.toLowerCase()}`,
        label: userEmail,
        sendFrom: { mode: 'user_mailbox', mailboxUpn: userEmail },
      });
    }
    return options;
  }, [arModuleConfig, currentUser]);

  // Org-level default BCC for AR statements, pre-filled (and overridable) in the
  // compose recipients below. Since statements now send from the current user's
  // mailbox, this is how a shared record copy is retained.
  //
  // Validated here (against the send contract, not the looser EMAIL_REGEX) so a
  // malformed entry saved in Settings can't reach the send API: the server
  // validates bccRecipientEmails with z.string().email() and 400s on a bad
  // address, which in the Send All fallback path would mark every unvisited
  // customer in the batch as failed. This is the single place the config BCC
  // list is cleaned — the compose pre-fill and Send All fallback both consume
  // it as-is.
  const outboxBccEmails = useMemo<string[]>(
    () =>
      (arModuleConfig?.config?.ar?.outboxBccEmails ?? []).flatMap((email) => {
        const trimmed = email.trim();
        return isSendableEmail(trimmed) ? [trimmed] : [];
      }),
    [arModuleConfig],
  );

  // How the customer's "Customer Statement"-tagged contacts are split across
  // To/Cc. The server resolves them as a flat ordered list, so this dialog is
  // where the roles are decided — which is what makes the setting apply to
  // every entry point that opens it. The decided split is then sent explicitly
  // (recipientEmails = To, ccRecipientEmails = Cc) so the server addresses the
  // email exactly as displayed.
  //
  // Precedence: a caller's override (a payment-reminder workflow step that
  // configured its own mode) beats the org's AR setting, which beats the
  // built-in default.
  const statementRecipientRoles =
    statementRecipientRolesOverride ??
    arModuleConfig?.config?.ar?.statementRecipientRoles ??
    AR_DEFAULT_STATEMENT_RECIPIENT_ROLES;

  useEffect(() => {
    if (sendFromOptions.length === 0) {
      setSelectedSendFromKey('');
      return;
    }
    if (!sendFromOptions.some((o) => o.key === selectedSendFromKey)) {
      // Default to the current user's own mailbox when available, falling back
      // to the configured AR outbox.
      const userOption = sendFromOptions.find((o) => o.key.startsWith('user:'));
      setSelectedSendFromKey((userOption ?? sendFromOptions[0]).key);
    }
  }, [sendFromOptions, selectedSendFromKey]);

  const selectedSendFrom = useMemo(
    () => sendFromOptions.find((o) => o.key === selectedSendFromKey)?.sendFrom,
    [sendFromOptions, selectedSendFromKey],
  );

  const currentCustomer = customers[currentIndex] ?? customers[0];
  // Result of the customer currently under review, if any. Only non-empty when
  // navigating to an already-actioned customer via the rail or summary. Drives
  // the read-only "sent" review below (showing what was emailed) and the
  // "Re-add to batch" footer for canceled customers.
  const currentResult = currentCustomer
    ? results.get(currentCustomer.customerId)
    : undefined;
  const currentCustomerStatus = currentResult?.status;
  // When reviewing a sent customer, load the persisted outbound email so the
  // review surface can show exactly what went out (read-only) instead of the
  // editable compose UI.
  const sentOutboundEmailId =
    currentCustomerStatus === 'sent'
      ? currentResult?.outboundEmailId
      : undefined;
  const { emailData: sentEmailData, isLoading: sentEmailLoading } =
    useOutboundEmailViewData(sentOutboundEmailId, sentOutboundEmailId != null);

  const { contacts, isLoading: contactsLoading } = useArCustomerContacts(
    currentCustomer?.customerId ?? '',
  );
  const { defaultRecipientEmails, isLoading: defaultsLoading } =
    useArStatementDefaults(currentCustomer?.customerId);
  const { send, isSending, preview, isPreviewing, previewData, resetPreview } =
    useArStatementEmail();

  // ── Refresh invoices from Famous ────────────────────────────────────────
  // Two independent refresh paths:
  //  • Open-time: a customer-scoped refresh (refreshHeadersForCustomers) where
  //    the server resolves every open invoice for the customers in scope. The
  //    dialog never ships an invoice-id array.
  //  • Post-preview: an invoice-id-scoped refresh (refreshUnseenInvoices →
  //    refreshHeaders) for IDs the dialog's own filter brought into scope after
  //    open, deduped via refreshedInvoiceIdsRef. Resets when the dialog closes.
  const refreshHeadersMutation = useRefreshArInvoiceHeadersFromFamous();
  const refreshHeadersForCustomersMutation =
    useRefreshArInvoiceHeadersForCustomersFromFamous();
  const refreshedInvoiceIdsRef = useRef<Set<string>>(new Set());

  // Pull out the stable `mutateAsync` reference. react-query returns a new
  // mutation object on every status transition (idle → pending → error), but
  // `mutateAsync` keeps a stable identity. Depending on the object below would
  // change `refreshUnseenInvoices`'s identity on each transition, re-firing
  // the post-preview effect; combined with the rollback in the catch that
  // loops forever when the refresh hard-fails (e.g. no Oracle credentials).
  const { mutateAsync: refreshHeaders } = refreshHeadersMutation;
  const { mutateAsync: refreshHeadersForCustomers } =
    refreshHeadersForCustomersMutation;

  const refreshUnseenInvoices = useCallback(
    async (ids: string[]): Promise<boolean> => {
      const unseen = ids.filter(
        (id) => !refreshedInvoiceIdsRef.current.has(id),
      );
      if (unseen.length === 0) return false;
      // Mark optimistically so concurrent callers don't double-refresh the
      // same IDs (e.g. React StrictMode double-invoking an effect).
      unseen.forEach((id) => refreshedInvoiceIdsRef.current.add(id));
      try {
        await refreshHeaders({ ids: unseen });
        return true;
      } catch {
        // Roll back so a retry on the next trigger has a chance.
        unseen.forEach((id) => refreshedInvoiceIdsRef.current.delete(id));
        return false;
      }
    },
    [refreshHeaders],
  );

  // Reset the post-preview dedupe set and both refresh mutations when the
  // dialog closes, so a reopen starts clean. The customer-scoped fleet refresh
  // is NOT fired on open — that would make opening the dialog to review one
  // customer wait on a full-fleet Oracle refresh (see handleSendAll, which
  // runs it only when the user commits to "Send All"). Per-customer review
  // stays fast: each viewed customer is refreshed by the post-preview path.
  useEffect(() => {
    if (open) return;
    refreshedInvoiceIdsRef.current = new Set();
    refreshHeadersMutation.reset();
    refreshHeadersForCustomersMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Preview auto-fetch ──────────────────────────────────────────────────

  // Hold the last successful preview so content stays mounted during
  // refetches. We don't read previewData directly because concurrent
  // mutations can resolve out of order — a stale resolution would otherwise
  // stomp the iframe's data URL and cause the PDF to reload twice. Seed
  // from previewData on first render to pick up any cached value.
  const [displayPreview, setDisplayPreview] = useState<typeof previewData>(
    () => previewData,
  );
  // The content key of whatever `displayPreview` currently holds (null until
  // the first preview resolves). Compared against the current key below to
  // gate sending on a fresh preview.
  const [displayPreviewKey, setDisplayPreviewKey] = useState<string | null>(
    null,
  );

  // Capture the full invoice list (before exclusions) so the Invoices tab
  // can render all rows client-side without waiting for preview refreshes.
  // Snapshotted into the customer's draft so a return visit doesn't have to
  // re-derive it from a preview (see CustomerDraft.allInvoices).
  const [allInvoices, setAllInvoices] = useState<ArInvoiceListItem[]>([]);

  const fetchGenRef = useRef(0);

  const fetchPreview = useCallback(() => {
    if (!currentCustomer) return;
    setPreviewError(false);
    const gen = ++fetchGenRef.current;
    const hasExclusions = excludedInvoiceIds.size > 0;
    const contentKey = buildPreviewContentKey(
      currentCustomer.customerId,
      effectiveReportConfig,
      excludedInvoiceIds,
    );
    const highlightsPayload =
      invoiceHighlights.size > 0
        ? Array.from(invoiceHighlights.entries()).map(([invoiceId, color]) => ({
            invoiceId,
            color,
          }))
        : undefined;
    preview({
      customerId: currentCustomer.customerId,
      recipientEmails: [PLACEHOLDER_EMAIL],
      ...arAgingPayload(effectiveReportConfig),
      ...(hasExclusions && {
        excludeInvoiceIds: Array.from(excludedInvoiceIds),
      }),
      ...(highlightsPayload && { invoiceHighlights: highlightsPayload }),
      ...(selectedSendFrom && { sendFrom: selectedSendFrom }),
    })
      .then((data) => {
        if (fetchGenRef.current !== gen) return;
        setDisplayPreview(data);
        setDisplayPreviewKey(contentKey);
        if (!hasExclusions) {
          setAllInvoices(data.invoices);
        }
      })
      .catch(() => {
        if (fetchGenRef.current === gen) setPreviewError(true);
      });
  }, [
    currentCustomer,
    effectiveReportConfig,
    excludedInvoiceIds,
    invoiceHighlights,
    selectedSendFrom,
    preview,
  ]);

  // After each preview returns, refresh any invoice IDs we haven't yet seen
  // this session (e.g. ones brought into scope by the dialog's own filter
  // changes after open). If anything was refreshed, re-fetch the preview so
  // the user sees the now-fresh amounts/balances. The cleanup flag prevents
  // a stale refresh from firing fetchPreview after the dialog has closed or
  // the user has navigated to a different customer.
  useEffect(() => {
    if (!open || !displayPreview) return;
    const ids = displayPreview.invoices.map((i) => i.id);
    if (ids.length === 0) return;
    let cancelled = false;
    void refreshUnseenInvoices(ids).then((didRefresh) => {
      if (!cancelled && didRefresh) fetchPreview();
    });
    return () => {
      cancelled = true;
    };
  }, [open, displayPreview, refreshUnseenInvoices, fetchPreview]);

  const lastFetchedCustomerId = useRef<string | null>(null);
  useEffect(() => {
    if (!open || phase !== 'review') return;
    const cid = currentCustomer?.customerId ?? null;
    if (!cid) return;
    if (lastFetchedCustomerId.current !== cid) {
      // New customer — fetch immediately. Leave displayPreview in place so
      // the previous PDF stays visible until the new one resolves; the
      // gen-gated .then will swap it in, avoiding an unmount/remount of
      // the iframe.
      lastFetchedCustomerId.current = cid;
      resetPreview();
      fetchPreview();
      return;
    }
    // Report settings changed — debounce refresh
    const timer = setTimeout(fetchPreview, 600);
    return () => clearTimeout(timer);
  }, [open, phase, currentCustomer, fetchPreview, resetPreview]);

  // ── Per-customer draft persistence ───────────────────────────────────────
  // captureCurrentDraft snapshots the editable per-customer state into a
  // CustomerDraft, and persistCurrentDraft stores it in the drafts map under
  // the current customer's id. Called before any navigation away.

  function captureCurrentDraft(): CustomerDraft {
    return {
      selectedEmails: new Map(selectedEmails),
      customRecipients: customRecipients.map((r) => ({ ...r })),
      excludedInvoiceIds: new Set(excludedInvoiceIds),
      subjectDraft,
      bodyDraft,
      pdfFilenameDraft,
      invoiceHighlights: new Map(invoiceHighlights),
      bccSender,
      selectedSendFromKey,
      allInvoices,
      seededSubject: seededSubjectRef.current,
      baselineBodyMustache: baselineBodyMustacheRef.current,
    };
  }

  // True if the user edited the email subject or body away from the
  // platform-generated baseline. Reported to the server as `editedBody` so the
  // statement-customization telemetry is accurate — the send payload always
  // carries subject/body overrides, so the server can't infer this itself. The
  // body is compared in mustache form so auto-updated template-variable values
  // (totals/counts) don't read as edits.
  function computeEditedBody(args: {
    subjectDraft: string;
    bodyDraft: string;
    seededSubject: string;
    baselineBodyMustache: string;
  }): boolean {
    const subjectChanged =
      args.subjectDraft.trim() !== args.seededSubject.trim();
    const baseline = args.baselineBodyMustache.trim();
    // An empty baseline means the body editor never mounted (the user never
    // opened the Email Body tab) — so the body is definitionally unedited. Only
    // compare once we have a real captured baseline.
    const bodyChanged =
      baseline !== '' &&
      templateVariableMarkersToMustache(args.bodyDraft).trim() !== baseline;
    return subjectChanged || bodyChanged;
  }

  // The body editor emits onChange on mount (normalizing the seeded HTML to
  // markers) and again whenever template variables auto-update. Capture the
  // first emission per customer as the unedited baseline; later emissions are
  // either real user edits or value-only updates, which the mustache comparison
  // in computeEditedBody handles.
  function handleBodyChange(html: string) {
    setBodyDraft(html);
    const cid = currentCustomer?.customerId;
    if (!cid) return;
    if (!bodyBaselineCapturedCustomers.current.has(cid)) {
      bodyBaselineCapturedCustomers.current.add(cid);
      const baseline = templateVariableMarkersToMustache(html).trim();
      baselineBodyMustacheRef.current = baseline;
      bodyBaselineByCustomer.current.set(cid, baseline);
      return;
    }
    // Baseline already captured for this customer, so this is a later emission.
    // Emit body_edited once per customer, and only for a genuine content change
    // — the mustache compare ignores auto-updated template-variable values
    // (totals/counts) exactly as computeEditedBody does. Compare against this
    // customer's own baseline so a return visit can't read another's.
    if (bodyEditedCustomers.current.has(cid)) return;
    const baseline = bodyBaselineByCustomer.current.get(cid) ?? '';
    const bodyChanged =
      baseline !== '' &&
      templateVariableMarkersToMustache(html).trim() !== baseline;
    if (bodyChanged) {
      bodyEditedCustomers.current.add(cid);
      sessionEngagedRef.current = true;
      trackStatement(statementSteps.bodyEdited, { customerId: cid });
    }
  }

  // Subject <input> onChange fires per keystroke; emit subject_edited only the
  // first time a given customer's subject is touched. Programmatic seeds/restores
  // call setSubjectDraft directly and so never trip this.
  function handleSubjectChange(value: string) {
    setSubjectDraft(value);
    const cid = currentCustomer?.customerId;
    if (!cid || subjectEditedCustomers.current.has(cid)) return;
    subjectEditedCustomers.current.add(cid);
    sessionEngagedRef.current = true;
    trackStatement(statementSteps.subjectEdited, { customerId: cid });
  }

  function persistCurrentDraft() {
    if (!currentCustomer) return;
    const draft = captureCurrentDraft();
    setCustomerDrafts((prev) => {
      const next = new Map(prev);
      next.set(currentCustomer.customerId, draft);
      return next;
    });
  }

  // ── Restore draft on entering a customer ─────────────────────────────────
  // If we have a saved draft for the customer being entered, restore it and
  // mark the seeded/applied sets so the seed and apply-defaults effects
  // below skip and don't clobber the restored values. Gates are keyed by
  // customerId, not currentIndex — keying by index can leak data when
  // a customer with empty defaults shares an index with one that has them.
  //
  // The gate is per *visit*, not per session: leaveCurrentCustomer clears the
  // entry for the customer being left, so the next entry restores again. It
  // only has to survive the renders of a single visit (customerDrafts changes
  // when another customer's draft is persisted, which re-runs this effect and
  // would otherwise clobber live edits with the stale snapshot).
  const restoredDraftCustomers = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!open || phase !== 'review') return;
    if (!currentCustomer) return;
    const cid = currentCustomer.customerId;
    if (restoredDraftCustomers.current.has(cid)) return;
    const draft = customerDrafts.get(cid);
    if (!draft) return;
    restoredDraftCustomers.current.add(cid);
    setSelectedEmails(new Map(draft.selectedEmails));
    setCustomRecipients(draft.customRecipients.map((r) => ({ ...r })));
    setExcludedInvoiceIds(new Set(draft.excludedInvoiceIds));
    setSubjectDraft(draft.subjectDraft);
    setBodyDraft(draft.bodyDraft);
    setPdfFilenameDraft(draft.pdfFilenameDraft);
    setInvoiceHighlights(new Map(draft.invoiceHighlights));
    setBccSender(draft.bccSender);
    setSelectedSendFromKey(draft.selectedSendFromKey);
    setAllInvoices(draft.allInvoices);
    seededSubjectRef.current = draft.seededSubject;
    baselineBodyMustacheRef.current = draft.baselineBodyMustache;
    // Baseline already established for this customer — don't let the editor's
    // post-remount onChange recapture it from a possibly-edited body.
    bodyBaselineCapturedCustomers.current.add(cid);
    seededDraftCustomers.current.add(cid);
    appliedDefaultsCustomers.current.add(cid);
  }, [open, phase, currentCustomer, customerDrafts]);

  // ── Seed email drafts on first preview per customer ──────────────────────
  // Only set the subject/body once per customer so user edits are preserved
  // across invoice-list or report-config changes. The
  // UpdateTemplateVariablesPlugin handles updating interpolated values
  // in-place inside the Lexical editor.

  const seededDraftCustomers = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!open) return;
    if (!previewData || isPreviewing) return;
    if (!currentCustomer) return;
    const cid = currentCustomer.customerId;
    if (seededDraftCustomers.current.has(cid)) return;
    seededDraftCustomers.current.add(cid);
    setSubjectDraft(previewData.subject);
    setBodyDraft(previewData.htmlContent);
    setPdfFilenameDraft(previewData.pdfFilename);
    seededSubjectRef.current = previewData.subject;
  }, [open, previewData, currentCustomer, isPreviewing]);

  // Close this gate even when the customer has no saved defaults — otherwise
  // a later cache refetch or contacts-reference change would re-fire the
  // effect and could clobber recipients the user typed in manually.
  // Also gated on `open` so that the contacts/defaults hooks firing while
  // the dialog is closed (as the user toggles selection in the report) don't
  // silently populate selectedEmails for whoever is currentCustomer at the
  // moment — which would then leak into the next customer to take that slot.
  const appliedDefaultsCustomers = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!open) return;
    // Wait for the AR module config too: the gate below closes permanently per
    // customer, so applying defaults while the config is still in flight would
    // lock in the fallback To/Cc mode and an empty Bcc pre-fill. isPending
    // settles on error as well, so a failed config load still falls back.
    if (contactsLoading || defaultsLoading || arModuleConfigPending) return;
    if (!currentCustomer) return;
    const cid = currentCustomer.customerId;
    if (appliedDefaultsCustomers.current.has(cid)) return;
    appliedDefaultsCustomers.current.add(cid);

    // To/Cc: the customer's "Customer Statement"-tagged contacts that are also
    // in the contact list, split across To/Cc per the org's configured mode
    // (default: first becomes To, the rest Cc).
    if (defaultRecipientEmails.length > 0) {
      const availableEmails = new Set(
        contacts
          .map((c) => c.email?.toLowerCase())
          .filter((e): e is string => e !== undefined && e !== ''),
      );
      const toSelect = defaultRecipientEmails.filter((e) =>
        availableEmails.has(e.toLowerCase()),
      );
      if (toSelect.length > 0) {
        setSelectedEmails(
          assignStatementRecipientRoles(toSelect, statementRecipientRoles),
        );
      }
    }

    // Bcc: pre-fill from the AR module config default (overridable). Org-level,
    // so it's the same for every customer; independent of whether the customer
    // has tagged To/Cc contacts. outboxBccEmails is already trimmed and
    // validated at its memo, so it's the single place this list is cleaned.
    const bccRecipients = outboxBccEmails.map((email) => ({
      id: makeRecipientId(),
      email,
      role: 'bcc' as const,
    }));
    if (bccRecipients.length > 0) {
      setCustomRecipients(bccRecipients);
    }
  }, [
    open,
    contactsLoading,
    defaultsLoading,
    arModuleConfigPending,
    defaultRecipientEmails,
    outboxBccEmails,
    statementRecipientRoles,
    contacts,
    currentCustomer,
  ]);

  // ── Recipients logic ────────────────────────────────────────────────────

  const dedupedContacts = useMemo(() => {
    const map = new Map<
      string,
      { id: string; email: string; tags: string[] }
    >();
    for (const c of contacts) {
      if (!c.email || !EMAIL_REGEX.test(c.email.trim())) continue;
      const key = c.email.trim().toLowerCase();
      const existing = map.get(key);
      const tagNames = (c.tags ?? [])
        .map((t) => t.name?.trim() ?? '')
        .filter((n) => n.length > 0);
      if (existing) {
        for (const t of tagNames) {
          if (!existing.tags.includes(t)) existing.tags.push(t);
        }
      } else {
        map.set(key, {
          id: c.id,
          email: key,
          tags: Array.from(new Set(tagNames)),
        });
      }
    }
    return Array.from(map.values());
  }, [contacts]);

  function addContactToRole(email: string, role: RecipientRole) {
    setSelectedEmails((prev) => {
      if (prev.get(email) === role) return prev;
      const next = new Map(prev);
      next.set(email, role);
      return next;
    });
  }

  function toggleEmail(email: string) {
    setSelectedEmails((prev) => {
      const next = new Map(prev);
      if (next.has(email)) {
        next.delete(email);
        // If we removed the last To, promote first CC
        if (![...next.values()].includes('to') && next.size > 0) {
          const firstKey = next.keys().next().value;
          if (firstKey !== undefined) {
            next.set(firstKey, 'to');
          }
        }
      } else {
        // First selected email becomes To, rest default to CC
        next.set(email, next.size === 0 ? 'to' : 'cc');
      }
      return next;
    });
  }

  function toggleEmailRole(email: string) {
    setSelectedEmails((prev) => {
      const next = new Map(prev);
      const current = next.get(email);
      if (!current) return prev;
      // Don't allow removing the last To
      if (
        current === 'to' &&
        [...prev.values()].filter((v) => v === 'to').length === 1
      ) {
        return prev;
      }
      next.set(email, current === 'to' ? 'cc' : 'to');
      return next;
    });
  }

  function removeContact(email: string) {
    setSelectedEmails((prev) => {
      if (!prev.has(email)) return prev;
      const next = new Map(prev);
      next.delete(email);
      // If we removed the last To, promote first remaining entry
      if (![...next.values()].includes('to') && next.size > 0) {
        const firstKey = next.keys().next().value;
        if (firstKey !== undefined) next.set(firstKey, 'to');
      }
      return next;
    });
  }

  function addCustomRecipient(email: string, role: RecipientRole) {
    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) return false;
    const lower = trimmed.toLowerCase();
    // Don't allow duplicate of an existing selected contact.
    if (selectedEmails.has(lower)) return false;
    // Don't allow duplicate within custom recipients.
    if (customRecipients.some((r) => r.email.toLowerCase() === lower)) {
      return false;
    }
    setCustomRecipients((prev) => [
      ...prev,
      { id: makeRecipientId(), email: trimmed, role },
    ]);
    return true;
  }

  function removeCustomRecipient(id: string) {
    setCustomRecipients((prev) => prev.filter((r) => r.id !== id));
  }

  const validCustomRecipients = customRecipients.filter((r) =>
    EMAIL_REGEX.test(r.email.trim()),
  );
  const selectedToEmails = [...selectedEmails.entries()]
    .filter(([, role]) => role === 'to')
    .map(([email]) => email);
  const customToEmails = validCustomRecipients
    .filter((r) => r.role === 'to')
    .map((r) => r.email.trim());
  const customCcEmails = validCustomRecipients
    .filter((r) => r.role === 'cc')
    .map((r) => r.email.trim());
  const customBccEmails = validCustomRecipients
    .filter((r) => r.role === 'bcc')
    .map((r) => r.email.trim());
  const toEmails = [...selectedToEmails, ...customToEmails];
  const ccEmails = [
    ...[...selectedEmails.entries()]
      .filter(([, role]) => role === 'cc')
      .map(([email]) => email),
    ...customCcEmails,
  ];
  const bccEmails = [
    ...[...selectedEmails.entries()]
      .filter(([, role]) => role === 'bcc')
      .map(([email]) => email),
    ...customBccEmails,
  ];
  const firstCustomEmail = validCustomRecipients[0]?.email.trim();
  const allRecipients = [...toEmails, ...ccEmails];
  const hasRecipients = allRecipients.length > 0;
  // Whether the shown preview is fresh for the customer/config the send would
  // use: it resolved (not loading), and its content key matches the current
  // one. `displayPreview` is retained across navigation/refetch to keep the PDF
  // mounted, so it can be null (initial load) or hold another customer's/older
  // config's data mid-fetch — neither is safe to send against.
  const currentPreviewKey = useMemo(
    () =>
      buildPreviewContentKey(
        currentCustomer?.customerId,
        effectiveReportConfig,
        excludedInvoiceIds,
      ),
    [currentCustomer, effectiveReportConfig, excludedInvoiceIds],
  );
  const isPreviewCurrent =
    displayPreview != null &&
    !isPreviewing &&
    displayPreviewKey === currentPreviewKey;
  // Block sending a statement that would contain no invoices (mirrors the
  // server-side guard in sendArStatementEmail). Only meaningful once a fresh
  // preview for the current customer/config has resolved.
  const hasNoMatchingInvoices =
    isPreviewCurrent && displayPreview.invoices.length === 0;

  // ── Navigation ──────────────────────────────────────────────────────────

  function resetTransientState() {
    setSelectedEmails(new Map());
    setCustomRecipients([]);
    // The panel's internal inline-add state resets via its key prop (keyed
    // on customerId), so no need to reset it here.
    setExcludedInvoiceIds(new Set());
    setAllInvoices([]);
    setPreviewError(false);
    setShowConfirm(false);
    setPreviewTab('invoices');
    setSubjectDraft('');
    setBodyDraft('');
    setPdfFilenameDraft('');
    setInvoiceHighlights(new Map());
    setBccSender(false);
  }

  function cycleHighlight(invoiceId: string) {
    // Derive the next color inside the updater from `prev` (the latest queued
    // state) so rapid clicks cycle correctly under batched/concurrent renders.
    setInvoiceHighlights((prev) => {
      const next = new Map(prev);
      const current = next.get(invoiceId) ?? null;
      const idx = HIGHLIGHT_CYCLE.indexOf(current);
      const nextColor =
        HIGHLIGHT_CYCLE[(idx + 1) % HIGHLIGHT_CYCLE.length] ?? null;
      if (nextColor === null) {
        next.delete(invoiceId);
      } else {
        next.set(invoiceId, nextColor);
      }
      return next;
    });
    // Telemetry is best-effort: a state updater's result isn't readable
    // synchronously, so report the transition from the rendered color the user
    // clicked. The authoritative cycle above still derives from `prev`.
    const shown = invoiceHighlights.get(invoiceId) ?? null;
    const reportedColor =
      HIGHLIGHT_CYCLE[
        (HIGHLIGHT_CYCLE.indexOf(shown) + 1) % HIGHLIGHT_CYCLE.length
      ] ?? null;
    sessionEngagedRef.current = true;
    trackStatement(statementSteps.highlightApplied, {
      customerId: currentCustomer?.customerId,
      invoiceId,
      color: reportedColor ?? 'none',
    });
  }

  function findPrevUnsentIndex(fromIndex: number): number | null {
    for (let i = fromIndex - 1; i >= 0; i--) {
      const cid = customers[i].customerId;
      if (results.get(cid)?.status === 'sent') continue;
      return i;
    }
    return null;
  }

  function findNextUnsentIndex(fromIndex: number): number | null {
    // Treat any existing result (sent / skipped / failed) as "reviewed" so
    // auto-advance lands on truly untouched customers only.
    for (let i = fromIndex + 1; i < customers.length; i++) {
      if (!results.has(customers[i].customerId)) return i;
    }
    // Wrap around to the start so untouched customers earlier in the list
    // (e.g. when the user jumped via the rail) aren't stranded.
    for (let i = 0; i < fromIndex; i++) {
      if (!results.has(customers[i].customerId)) return i;
    }
    return null;
  }

  const prevUnsentIndex = findPrevUnsentIndex(currentIndex);
  const canGoBack = !isSingle && prevUnsentIndex !== null;

  // Everything that has to happen before currentIndex moves: snapshot the
  // customer's edits, drop the live state they were held in, and re-open their
  // restore gate so re-entering them replays the snapshot. Without the last
  // step the gate stays closed from the first return visit onward and every
  // later visit shows blank state — highlights, exclusions and recipients all
  // silently reverted (guarded by "highlights come back on the second return
  // visit" in the tests).
  function leaveCurrentCustomer() {
    // Only the review phase holds live per-customer edits. Entering the summary
    // already reset that state, so persisting from there would write blanks
    // over the draft of whoever currentIndex still points at.
    if (phase === 'review') persistCurrentDraft();
    resetPreview();
    resetTransientState();
    if (currentCustomer) {
      restoredDraftCustomers.current.delete(currentCustomer.customerId);
    }
  }

  function advanceToNext() {
    leaveCurrentCustomer();
    const nextIdx = findNextUnsentIndex(currentIndex);
    if (nextIdx !== null) {
      setCurrentIndex(nextIdx);
    } else {
      setPhase('summary');
    }
  }

  function goBack() {
    if (prevUnsentIndex === null) return;
    leaveCurrentCustomer();
    setCurrentIndex(prevUnsentIndex);
  }

  // Open the summary mid-flow, before every customer has been actioned. The
  // summary is a detour, not an exit, so the customer being left goes through
  // the same snapshot path as a normal advance and their draft replays when
  // they're re-entered.
  function goToSummary() {
    if (phase === 'summary') return;
    // An already-sent customer is being shown read-only, so there is no live
    // draft to snapshot — persisting here would overwrite their stored draft
    // with the blank state that read-only view runs on. Same reason the
    // sent-review footer's "Back to summary" flips the phase directly.
    if (currentCustomerStatus !== 'sent') leaveCurrentCustomer();
    setPhase('summary');
  }

  // Navigate to a customer from the rail without clearing their existing
  // result. Skipped/sent/failed status is sticky — to drop it the user must
  // explicitly Send or use the summary's Revisit button.
  function viewCustomer(index: number) {
    if (index < 0 || index >= customers.length) return;
    if (index === currentIndex && phase === 'review') return;
    leaveCurrentCustomer();
    setCurrentIndex(index);
    setPhase('review');
  }

  function revisitCustomer(index: number) {
    if (index < 0 || index >= customers.length) return;
    const customer = customers[index];
    setResults((prev) => {
      const next = new Map(prev);
      next.delete(customer.customerId);
      return next;
    });
    // Re-open all gates for this specific customerId so the seed and
    // apply-defaults effects fire fresh on revisit.
    lastFetchedCustomerId.current = null;
    seededDraftCustomers.current.delete(customer.customerId);
    appliedDefaultsCustomers.current.delete(customer.customerId);
    restoredDraftCustomers.current.delete(customer.customerId);
    bodyBaselineCapturedCustomers.current.delete(customer.customerId);
    resetPreview();
    resetTransientState();
    setCurrentIndex(index);
    setPhase('review');
  }

  // Record a successful send for a customer and notify the caller. Shared by the
  // step-through (handleConfirmedSend) and bulk (handleSendAll) paths so the
  // "sent" result shape and the onStatementSent signal stay in one place.
  function markCustomerSent(
    customerId: string,
    customerName: string | null,
    outboundEmailId?: string,
    summary?: ArStatementSendSummary,
  ) {
    sessionSentRef.current = true;
    setResults((prev) => {
      const next = new Map(prev);
      next.set(customerId, {
        status: 'sent',
        customerName,
        outboundEmailId,
        summary,
      });
      return next;
    });
    onStatementSent?.();
  }

  async function handleConfirmedSend() {
    if (!hasRecipients || !currentCustomer) return;
    // Re-check the freshness gate the Send button enforces: never issue a send
    // against a stale or still-loading preview. The server resolves the invoice
    // set from the current config, so firing here would send a result the user
    // hasn't actually seen (and could be zero-invoice, caught only at the 400).
    if (!isPreviewCurrent || hasNoMatchingInvoices) return;
    trackStatement(statementSteps.sendClicked, {
      mode: 'single',
      customerId: currentCustomer.customerId,
      excludedInvoiceCount: excludedInvoiceIds.size,
      highlightCount: invoiceHighlights.size,
    });
    const trimmedSubject = subjectDraft.trim();
    const trimmedBody = templateVariableMarkersToMustache(bodyDraft).trim();
    const trimmedPdfFilename = pdfFilenameDraft.trim();
    const highlightsPayload =
      invoiceHighlights.size > 0
        ? Array.from(invoiceHighlights.entries()).map(([invoiceId, color]) => ({
            invoiceId,
            color,
          }))
        : undefined;
    try {
      const sendResult = await send({
        customerId: currentCustomer.customerId,
        ...buildStatementRecipientPayload({ toEmails, ccEmails }),
        ...arAgingPayload(effectiveReportConfig),
        ...(excludedInvoiceIds.size > 0 && {
          excludeInvoiceIds: Array.from(excludedInvoiceIds),
        }),
        ...(trimmedSubject && { subjectOverride: trimmedSubject }),
        ...(trimmedBody && { htmlContentOverride: trimmedBody }),
        ...(trimmedPdfFilename && {
          pdfFilenameOverride: trimmedPdfFilename,
        }),
        editedBody: computeEditedBody({
          subjectDraft,
          bodyDraft,
          seededSubject: seededSubjectRef.current,
          baselineBodyMustache: baselineBodyMustacheRef.current,
        }),
        ...(highlightsPayload && { invoiceHighlights: highlightsPayload }),
        ...(selectedSendFrom && { sendFrom: selectedSendFrom }),
        bccSender,
        ...(bccEmails.length > 0 && { bccRecipientEmails: bccEmails }),
        ...(firstCustomEmail && { customEmail: firstCustomEmail }),
        ...(importance !== 'normal' && { importance }),
      });
      if (isSingle) {
        sessionSentRef.current = true;
        onStatementSent?.();
        setShowConfirm(false);
        handleOpenChange(false);
        showToast({
          title: gt('Statement sent', { context: 'toast notification title' }),
          message: gt('Statement emailed to {recipients}', {
            context: 'toast notification message',
            recipients: allRecipients.join(', '),
          }),
          color: 'success',
          icon: 'check',
        });
        return;
      }
      markCustomerSent(
        currentCustomer.customerId,
        currentCustomer.customerName,
        sendResult.outboundEmailId,
        sendResult.summary,
      );
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : gt('Failed to send statement', {
              context: 'error message when statement send fails',
            });
      if (isSingle) {
        showToast({
          title: gt('Send failed', {
            context: 'toast notification title when statement send fails',
          }),
          message,
          color: 'error',
          icon: 'warning',
        });
        return;
      }
      setResults((prev) => {
        const next = new Map(prev);
        next.set(currentCustomer.customerId, {
          status: 'failed',
          customerName: currentCustomer.customerName,
          error: message,
        });
        return next;
      });
    }
    advanceToNext();
  }

  function handleSkip() {
    if (!currentCustomer) return;
    setResults((prev) => {
      const next = new Map(prev);
      next.set(currentCustomer.customerId, {
        status: 'skipped',
        customerName: currentCustomer.customerName,
      });
      return next;
    });
    advanceToNext();
  }

  function handleCancel() {
    if (!currentCustomer) return;
    setResults((prev) => {
      const next = new Map(prev);
      next.set(currentCustomer.customerId, {
        status: 'canceled',
        customerName: currentCustomer.customerName,
      });
      return next;
    });
    advanceToNext();
  }

  // Un-cancel: drop the customer's result so they return to "pending" and the
  // normal review/send controls come back. The customer's captured draft (if
  // any) is untouched, so their edits are still restored on the review screen.
  function readdToBatch(customerId: string) {
    setResults((prev) => {
      const next = new Map(prev);
      next.delete(customerId);
      return next;
    });
  }

  // ── Send all remaining ─────────────────────────────────────────────────

  const [isSendingAll, setIsSendingAll] = useState(false);
  // True during the up-front Famous refresh that precedes the Send All loop
  // (see handleSendAll). Distinguishes "refreshing invoice data" from the
  // per-customer "sending" phase in the button label.
  const [isPreparingSendAll, setIsPreparingSendAll] = useState(false);
  const [bulkSendProgress, setBulkSendProgress] = useState(0);
  const [showSendAllConfirm, setShowSendAllConfirm] = useState(false);

  // Pre-fetch all customer defaults in parallel
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _allDefaultsQueries = useQueries({
    queries: useMemo(
      () =>
        customers.map((customer) =>
          orpc.accountsReceivable.arStatementDefaults.get.queryOptions({
            input: { customerId: customer.customerId },
            query: { staleTime: 5 * 60 * 1000 },
          }),
        ),
      [customers],
    ),
  });

  // Customers the user hasn't reviewed yet. These will fall back to server
  // defaults during a "Send All" — surfaced as a warning before sending.
  // Canceled customers are excluded since Send All won't send them.
  const unvisitedCustomers = customers.filter((customer, i) => {
    const status = results.get(customer.customerId)?.status;
    if (status === 'sent' || status === 'canceled') return false;
    if (customerDrafts.has(customer.customerId)) return false;
    if (i === currentIndex) return false;
    return true;
  });
  const remainingCount = customers.filter((c) => {
    const status = results.get(c.customerId)?.status;
    return status !== 'sent' && status !== 'canceled';
  }).length;

  function getDraftRecipients(draft: CustomerDraft): StatementRecipientSplit {
    const toEmails: string[] = [];
    const ccEmails: string[] = [];
    for (const [email, role] of draft.selectedEmails) {
      if (role === 'to') toEmails.push(email);
      else if (role === 'cc') ccEmails.push(email);
    }
    for (const r of draft.customRecipients) {
      const trimmed = r.email.trim();
      if (!EMAIL_REGEX.test(trimmed)) continue;
      if (r.role === 'to') toEmails.push(trimmed);
      else if (r.role === 'cc') ccEmails.push(trimmed);
    }
    return { toEmails, ccEmails };
  }

  function getDraftBccRecipients(draft: CustomerDraft): string[] {
    const bccs: string[] = [];
    for (const [email, role] of draft.selectedEmails) {
      if (role === 'bcc') bccs.push(email);
    }
    for (const r of draft.customRecipients) {
      const trimmed = r.email.trim();
      if (r.role === 'bcc' && EMAIL_REGEX.test(trimmed)) bccs.push(trimmed);
    }
    return bccs;
  }

  function buildDraftSendArgs(
    customerId: string,
    draft: CustomerDraft,
    recipients: StatementRecipientSplit,
  ): Parameters<typeof send>[0] {
    const trimmedSubject = draft.subjectDraft.trim();
    const trimmedBody = templateVariableMarkersToMustache(
      draft.bodyDraft,
    ).trim();
    const trimmedPdfFilename = draft.pdfFilenameDraft.trim();
    const firstDraftCustom = draft.customRecipients
      .map((r) => r.email.trim())
      .find((e) => EMAIL_REGEX.test(e));
    const highlightsPayload =
      draft.invoiceHighlights.size > 0
        ? Array.from(draft.invoiceHighlights.entries()).map(
            ([invoiceId, color]) => ({ invoiceId, color }),
          )
        : undefined;
    const draftSendFrom = sendFromOptions.find(
      (o) => o.key === draft.selectedSendFromKey,
    )?.sendFrom;
    const draftBcc = getDraftBccRecipients(draft);
    return {
      customerId,
      ...buildStatementRecipientPayload(recipients),
      ...arAgingPayload(effectiveReportConfig),
      ...(draft.excludedInvoiceIds.size > 0 && {
        excludeInvoiceIds: Array.from(draft.excludedInvoiceIds),
      }),
      ...(trimmedSubject && { subjectOverride: trimmedSubject }),
      ...(trimmedBody && { htmlContentOverride: trimmedBody }),
      ...(trimmedPdfFilename && { pdfFilenameOverride: trimmedPdfFilename }),
      editedBody: computeEditedBody({
        subjectDraft: draft.subjectDraft,
        bodyDraft: draft.bodyDraft,
        seededSubject: draft.seededSubject,
        baselineBodyMustache: draft.baselineBodyMustache,
      }),
      ...(highlightsPayload && { invoiceHighlights: highlightsPayload }),
      ...(draftSendFrom && { sendFrom: draftSendFrom }),
      bccSender: draft.bccSender,
      ...(draftBcc.length > 0 && { bccRecipientEmails: draftBcc }),
      ...(firstDraftCustom && { customEmail: firstDraftCustom }),
      ...(importance !== 'normal' && { importance }),
    };
  }

  function handleSendAllClick() {
    setShowSendAllConfirm(true);
  }

  async function handleSendAll() {
    // The unvisited-customer fallback below reads statementRecipientRoles and
    // outboxBccEmails straight off the module config — sending while the query
    // is still in flight would apply the fallback role mode and drop the org's
    // default Bcc record copy. The Send All button is disabled while pending;
    // this also guards a confirmation dialog that was already open.
    if (arModuleConfigPending) return;
    setShowSendAllConfirm(false);
    trackStatement(statementSteps.sendClicked, {
      mode: 'bulk',
      customerCount: customers.length,
    });

    // Snapshot the current customer's in-progress edits so they're included
    // alongside previously-saved drafts. We persist into state too so navigating
    // back later still shows the user's edits.
    const drafts = new Map(customerDrafts);
    if (currentCustomer) {
      const liveDraft = captureCurrentDraft();
      drafts.set(currentCustomer.customerId, liveDraft);
      persistCurrentDraft();
    }

    setIsSendingAll(true);
    setBulkSendProgress(0);

    // Refresh every still-pending customer's invoices from Famous up front so
    // the statements we're about to send reflect the latest source-of-truth —
    // even for customers the user never opened (their per-customer post-preview
    // refresh never ran). The server resolves each customer's open invoices and
    // chunks the Oracle work; best-effort, so a Famous/Oracle problem falls
    // back to already-ingested data rather than blocking the send.
    if (refreshBeforeSendAll) {
      const pendingCustomerIds = customers
        .filter((c) => {
          const status = results.get(c.customerId)?.status;
          return status !== 'sent' && status !== 'canceled';
        })
        .map((c) => c.customerId);
      if (pendingCustomerIds.length > 0) {
        setIsPreparingSendAll(true);
        try {
          await refreshHeadersForCustomers({
            customerIds: pendingCustomerIds,
          });
        } catch {
          // Ignore — proceed with already-ingested data.
        }
        setIsPreparingSendAll(false);
      }
    }

    for (let i = 0; i < customers.length; i++) {
      const customer = customers[i];
      const status = results.get(customer.customerId)?.status;
      if (status === 'sent' || status === 'canceled') {
        setBulkSendProgress((prev) => prev + 1);
        continue;
      }

      try {
        const draft = drafts.get(customer.customerId);
        let payload: Parameters<typeof send>[0] | null = null;

        if (draft) {
          const draftRecipients = getDraftRecipients(draft);
          // Any chosen recipient — even a Cc-only pick — counts as a draft
          // selection: falling through to server defaults here would email
          // addresses the user deliberately deselected. A Cc-only draft goes
          // out as the legacy flat list (see buildStatementRecipientPayload),
          // exactly like the individual-send path handles it.
          if (
            draftRecipients.toEmails.length + draftRecipients.ccEmails.length >
            0
          ) {
            payload = buildDraftSendArgs(
              customer.customerId,
              draft,
              draftRecipients,
            );
          }
        }

        if (!payload) {
          // No draft, or draft had no recipients — fall back to server defaults.
          const defaults = await queryClient.fetchQuery(
            orpc.accountsReceivable.arStatementDefaults.get.queryOptions({
              input: { customerId: customer.customerId },
            }),
          );
          const emails = defaults?.defaultRecipientEmails ?? [];
          if (emails.length === 0) {
            setResults((prev) => {
              const next = new Map(prev);
              next.set(customer.customerId, {
                status: 'failed',
                customerName: customer.customerName,
                error: gt('No recipient email configured for this customer', {
                  context:
                    'error message when Send All has no recipient email to send to',
                }),
              });
              return next;
            });
            setBulkSendProgress((prev) => prev + 1);
            continue;
          }
          payload = {
            customerId: customer.customerId,
            // The server resolves defaults as a flat ordered list — apply the
            // org's configured To/Cc mode here, same as the compose dialog does
            // for visited customers.
            ...buildStatementRecipientPayload(
              splitStatementRecipients(emails, statementRecipientRoles),
            ),
            ...arAgingPayload(effectiveReportConfig),
            // Sent with platform defaults, never opened in the editor. Mirror
            // the dialog defaults: send from the current user's mailbox and BCC
            // the AR-config default recipients (not the sender).
            editedBody: false,
            ...(selectedSendFrom && { sendFrom: selectedSendFrom }),
            bccSender: false,
            ...(outboxBccEmails.length > 0 && {
              bccRecipientEmails: outboxBccEmails,
            }),
            ...(importance !== 'normal' && { importance }),
          };
        }

        const sendResult = await send(payload);
        markCustomerSent(
          customer.customerId,
          customer.customerName,
          sendResult.outboundEmailId,
          sendResult.summary,
        );
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : gt('Failed to send statement', {
                context: 'error message when statement send fails',
              });
        setResults((prev) => {
          const next = new Map(prev);
          next.set(customer.customerId, {
            status: 'failed',
            customerName: customer.customerName,
            error: message,
          });
          return next;
        });
      }
      setBulkSendProgress((prev) => prev + 1);
    }

    setIsSendingAll(false);
    resetTransientState();
    setPhase('summary');
  }

  // ── Dialog open/close ───────────────────────────────────────────────────

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && isSendingAll) return;
    if (!nextOpen) {
      // Closed without sending anything this session — record it (and whether
      // the user had customized) so we can see where journeys drop off.
      if (!sessionSentRef.current) {
        trackStatement(statementSteps.dialogAbandoned, {
          customerCount: customers.length,
          engaged: sessionEngagedRef.current,
        });
      }
      setCurrentIndex(0);
      setPhase('review');
      setResults(new Map());
      setCustomerDrafts(new Map());
      setShowSendAllConfirm(false);
      setIsPreparingSendAll(false);
      setImportance('normal');
      restoredDraftCustomers.current.clear();
      resetTransientState();
      setSelectedSendFromKey('');
      setReportConfig(reportSettings);
      lastFetchedCustomerId.current = null;
      appliedDefaultsCustomers.current.clear();
      seededDraftCustomers.current.clear();
      setDisplayPreview(null);
      setDisplayPreviewKey(null);
      resetPreview();
    }
    onOpenChange(nextOpen);
  }

  // ── Summary data ────────────────────────────────────────────────────────

  const resultEntries = Array.from(results.entries());
  const sentCount = resultEntries.filter(([, r]) => r.status === 'sent').length;
  const skippedCount = resultEntries.filter(
    ([, r]) => r.status === 'skipped',
  ).length;
  const failedCount = resultEntries.filter(
    ([, r]) => r.status === 'failed',
  ).length;
  const canceledCount = resultEntries.filter(
    ([, r]) => r.status === 'canceled',
  ).length;
  const pendingCount = Math.max(
    customers.length - sentCount - failedCount - skippedCount - canceledCount,
    0,
  );

  // Report rows for the summary's download/copy actions: one per customer a
  // statement actually went out to, in the dialog's alphabetical order.
  // Skipped/canceled/failed customers have no statement, so no row.
  const sentSummaries = customers.flatMap((customer) => {
    const summary = results.get(customer.customerId)?.summary;
    return summary ? [summary] : [];
  });

  // Where "Continue sending" picks the flow back up when the summary was opened
  // mid-flow: the customer we left, if they're still un-actioned, otherwise the
  // next untouched one.
  const customerAtCurrentIndex = customers[currentIndex];
  const resumeIndex =
    customerAtCurrentIndex && !results.has(customerAtCurrentIndex.customerId)
      ? currentIndex
      : findNextUnsentIndex(currentIndex);

  function handleDownloadSummary() {
    downloadCSV({
      data: buildBulkStatementSummaryRecords(sentSummaries),
      filename: 'statement-send-summary',
      includeTotals: false,
    });
  }

  async function handleCopySummary() {
    try {
      await navigator.clipboard.writeText(
        buildBulkStatementSummaryTsv(sentSummaries),
      );
      showToast({
        title: gt('Summary copied', {
          context: 'toast title after copying the bulk send summary report',
        }),
        color: 'success',
        icon: 'check',
      });
    } catch {
      showToast({
        title: gt('Copy failed', {
          context: 'toast title when copying the bulk send summary fails',
        }),
        color: 'error',
        icon: 'warning',
      });
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────

  if (!currentCustomer && phase === 'review') return null;

  // During review we count completed work via the results map. This stays
  // accurate regardless of how the user navigated to the current customer
  // (forward, back, or revisit-from-summary).
  // The summary reads as 100% only when it's the end of the run; opened mid-flow
  // it keeps reporting actual progress so the bar doesn't claim work that's
  // still pending.
  const progressPercent =
    customers.length > 0
      ? ((phase === 'summary' && pendingCount === 0
          ? customers.length
          : isSendingAll
            ? bulkSendProgress
            : results.size) /
          customers.length) *
        100
      : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      // Radix guarded dismissal on the content; Base UI reports the reason on
      // the root's change event, so the guard lives here now.
      disableBackdropClickClose={isSendingAll}
      disableEscapeKeyClose={isSendingAll}
    >
      <DialogContent
        className="sm:max-w-[min(1900px,97vw)] h-[90vh] flex flex-col"
        showCloseButton={!isSendingAll}
      >
        <div className="flex flex-1 min-h-0 gap-4">
          {/* ── Left rail ───────────────────────────────────────── */}
          <aside className="w-60 shrink-0 flex flex-col border-r pr-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <T context="bulk send dialog left rail eyebrow label">
                Bulk send · Statements
              </T>
            </p>
            <p className="mt-1 text-lg font-semibold">
              <Var>{customers.length}</Var>{' '}
              <Plural n={customers.length} one="customer" other="customers" />
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              <T context="bulk send dialog left rail status counts">
                <Var>{sentCount}</Var> sent · <Var>{pendingCount}</Var> pending
              </T>
              {failedCount > 0 && (
                <T context="bulk send dialog left rail failed count">
                  {' · '}
                  <Var>{failedCount}</Var> failed
                </T>
              )}
              {skippedCount > 0 && (
                <T context="bulk send dialog left rail skipped count">
                  {' · '}
                  <Var>{skippedCount}</Var> skipped
                </T>
              )}
              {canceledCount > 0 && (
                <T context="bulk send dialog left rail canceled count">
                  {' · '}
                  <Var>{canceledCount}</Var> canceled
                </T>
              )}
            </p>
            {!isSingle && (
              <Progress value={progressPercent} className="mt-3 h-1.5" />
            )}
            {/* Summary + export, reachable at any point in the run. Previously
                these lived only on the end-of-run summary page, which meant the
                CSV of "who got a statement" was unreachable until the last
                customer had been actioned — users couldn't find it. */}
            {!isSingle && (
              <div className="mt-3 flex flex-col gap-1 border-t pt-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="justify-start"
                  onClick={goToSummary}
                  disabled={isSendingAll || phase === 'summary'}
                >
                  <ListChecksIcon className="mr-1.5 h-4 w-4" />
                  <T context="button label, open the bulk send summary at any point during the run">
                    View summary
                  </T>
                </Button>
                {sentSummaries.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start text-xs text-muted-foreground"
                    onClick={handleDownloadSummary}
                    disabled={isSendingAll}
                  >
                    <DownloadSimpleIcon className="mr-1.5 h-3.5 w-3.5" />
                    <T context="button label, download the bulk send summary report as a CSV file">
                      Download CSV
                    </T>
                  </Button>
                )}
              </div>
            )}
            <ul className="mt-3 flex-1 min-h-0 overflow-y-auto -mx-1">
              {customers.map((c, i) => {
                /* eslint-disable i18next/no-literal-string -- Tailwind utility classes only */
                const result = results.get(c.customerId);
                const status = result?.status;
                const isActive = i === currentIndex && phase === 'review';
                let dotClass = 'border border-muted-foreground';
                if (status === 'sent') dotClass = 'bg-green-500';
                else if (status === 'failed') dotClass = 'bg-red-500';
                else if (status === 'skipped') dotClass = 'bg-amber-500';
                else if (status === 'canceled') dotClass = 'bg-gray-400';
                else if (isActive) dotClass = 'bg-foreground';
                const isFinishedStatus =
                  status === 'sent' ||
                  status === 'skipped' ||
                  status === 'canceled';
                const rowClass = [
                  'w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
                  'hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent',
                  isActive
                    ? 'bg-muted font-semibold border-l-2 border-primary'
                    : '',
                  isFinishedStatus && !isActive ? 'text-muted-foreground' : '',
                  status === 'canceled' && !isActive ? 'line-through' : '',
                  isSendingAll ? 'opacity-60 cursor-not-allowed' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <li key={c.customerId}>
                    <button
                      type="button"
                      onClick={() => viewCustomer(i)}
                      disabled={isSendingAll || isActive}
                      className={rowClass}
                    >
                      <span
                        className={`h-2 w-2 rounded-full shrink-0 ${dotClass}`}
                        aria-hidden
                      />
                      <span className="truncate">
                        {c.customerName ??
                          gt('(Unknown)', {
                            context: 'unknown customer name placeholder',
                          })}
                      </span>
                    </button>
                  </li>
                );
                /* eslint-enable i18next/no-literal-string */
              })}
            </ul>
          </aside>

          {/* ── Right column ────────────────────────────────────── */}
          <div className="flex-1 min-h-0 flex flex-col gap-4">
            <DialogHeader>
              {phase === 'summary' ? (
                <>
                  <DialogTitle>
                    {pendingCount > 0 ? (
                      <T context="dialog title, bulk statement send summary opened before every customer is done">
                        Bulk Send Summary
                      </T>
                    ) : (
                      <T context="dialog title, bulk statement send finished">
                        Bulk Send Complete
                      </T>
                    )}
                  </DialogTitle>
                  {pendingCount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      <T context="dialog subtitle, statements still awaiting review in the bulk send">
                        <Var>{pendingCount}</Var> of{' '}
                        <Var>{customers.length}</Var> still to review — the
                        report below covers what has gone out so far.
                      </T>
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-baseline gap-3">
                    <DialogTitle className="text-2xl font-semibold tracking-tight">
                      {currentCustomer.customerName ??
                        gt('(Unknown)', {
                          context: 'unknown customer name placeholder',
                        })}
                    </DialogTitle>
                    {!isSingle && (
                      <span className="shrink-0 rounded bg-muted px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <T context="dialog header progress badge, e.g. '2 of 3'">
                          <Var>{String(currentIndex + 1)}</Var> of{' '}
                          <Var>{String(customers.length)}</Var>
                        </T>
                      </span>
                    )}
                  </div>
                  {currentCustomer.lastStatementSentAt && (
                    <p className="text-xs text-muted-foreground">
                      <T context="dialog header, last time statement was sent">
                        Last sent{' '}
                        <Var>
                          {formatDateFn(
                            new Date(currentCustomer.lastStatementSentAt),
                            "MM/dd/yyyy 'at' h:mm a",
                          )}
                        </Var>
                      </T>
                    </p>
                  )}
                </>
              )}
            </DialogHeader>

            {/* Caller-supplied explanation of how this customer list was
                arrived at (e.g. the payment-reminder task pruning customers who
                already received a statement), so a list shorter than expected
                doesn't read as missing work. */}
            {notice && (
              <p className="shrink-0 rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                {notice}
              </p>
            )}

            {/* ── Sent (read-only) review ─────────────────────────────── */}
            {/* Reviewing an already-sent customer: show exactly what was
                emailed (from the persisted outbound email) in place of the
                editable compose UI, so a send can be confirmed without going
                to the summary page. */}
            {phase === 'review' && currentCustomerStatus === 'sent' && (
              <>
                <div className="min-h-0 flex-1 flex flex-col overflow-hidden rounded border">
                  <OutboundEmailViewerBody
                    emailData={sentEmailData}
                    isLoading={sentEmailLoading}
                  />
                </div>
                <DialogFooter className="flex justify-between sm:justify-between">
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircleIcon
                      className="h-4 w-4 text-green-600"
                      weight="fill"
                    />
                    <T context="review footer notice shown when viewing a statement that was already sent">
                      Sent — this is the statement that was emailed to the
                      customer.
                    </T>
                  </span>
                  <Button variant="outline" onClick={() => setPhase('summary')}>
                    <T context="button label, return to the bulk send summary from a sent-statement review">
                      Back to summary
                    </T>
                  </Button>
                </DialogFooter>
              </>
            )}

            {/* ── Review Phase ──────────────────────────────────────── */}
            {phase === 'review' && currentCustomerStatus !== 'sent' && (
              <>
                <div className="min-h-0 flex-1 flex flex-col gap-4 overflow-hidden">
                  {/* Recipients (left) + Contacts (right) */}
                  <EmailRecipientsPanel
                    key={currentCustomer.customerId}
                    contactsLoading={contactsLoading}
                    contacts={dedupedContacts}
                    selectedEmails={selectedEmails}
                    customRecipients={customRecipients}
                    addCustomRecipient={addCustomRecipient}
                    addContactToRole={addContactToRole}
                    removeCustomRecipient={removeCustomRecipient}
                    removeContact={removeContact}
                    toggleEmail={toggleEmail}
                    toggleEmailRole={toggleEmailRole}
                    bccSender={bccSender}
                    setBccSender={setBccSender}
                    senderEmail={currentUser?.emails[0] ?? null}
                    sendFromOptions={sendFromOptions.map((o) => ({
                      key: o.key,
                      label: o.label,
                    }))}
                    selectedSendFromKey={selectedSendFromKey}
                    setSelectedSendFromKey={setSelectedSendFromKey}
                  />

                  {/* Report Settings */}
                  <div className="flex items-start gap-6 flex-wrap px-1">
                    <ArReportSettings
                      value={effectiveReportConfig}
                      onChange={setReportConfig}
                      lockBasis={basisLock.lockBasis}
                    />
                  </div>

                  {/* Preview */}
                  {!displayPreview && isPreviewing ? (
                    <div className="flex flex-1 min-h-0 items-center justify-center gap-2 text-sm text-muted-foreground">
                      <SpinnerIcon className="h-4 w-4 animate-spin" />
                      <T context="send statement preview loading state">
                        Loading preview...
                      </T>
                    </div>
                  ) : !displayPreview && previewError ? (
                    <div className="flex flex-1 min-h-0 items-center justify-center gap-2 text-sm text-muted-foreground">
                      <WarningIcon className="h-4 w-4" />
                      <T context="send statement preview error state">
                        Failed to load preview
                      </T>
                    </div>
                  ) : displayPreview ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground shrink-0">
                          <T context="email header Subject field">Subject:</T>
                        </span>
                        <Input
                          value={subjectDraft}
                          onChange={(e) => handleSubjectChange(e.target.value)}
                          className="h-7 text-xs"
                        />
                        {isPreviewing && (
                          <SpinnerIcon className="h-3 w-3 animate-spin shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground shrink-0">
                          <T context="email PDF attachment filename field label">
                            Attachment:
                          </T>
                        </span>
                        <Input
                          value={pdfFilenameDraft}
                          onChange={(e) => setPdfFilenameDraft(e.target.value)}
                          className="h-7 text-xs"
                          placeholder={gt('statement.pdf', {
                            context: 'placeholder for PDF attachment filename',
                          })}
                        />
                      </div>
                      <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                        <Checkbox
                          checked={importance === 'high'}
                          onCheckedChange={(v) =>
                            setImportance(v === true ? 'high' : 'normal')
                          }
                        />
                        <FlagIcon
                          weight={importance === 'high' ? 'fill' : 'regular'}
                          className={`h-3.5 w-3.5 ${
                            importance === 'high'
                              ? 'text-destructive'
                              : 'text-muted-foreground'
                          }`}
                        />
                        <span
                          className={
                            importance === 'high'
                              ? 'font-medium text-destructive'
                              : 'text-muted-foreground'
                          }
                        >
                          <T context="checkbox, mark outgoing statement email as high priority">
                            Mark as high priority
                          </T>
                        </span>
                      </label>
                      <Tabs
                        value={previewTab}
                        onValueChange={setPreviewTab}
                        className="min-h-0 flex-1 flex flex-col"
                      >
                        <TabsList className="w-fit">
                          <TabsTrigger value="invoices">
                            <T context="send statement preview tab">Invoices</T>
                            {allInvoices.length > 0 && (
                              <span className="ml-1.5 text-muted-foreground">
                                ({allInvoices.length - excludedInvoiceIds.size})
                              </span>
                            )}
                          </TabsTrigger>
                          <TabsTrigger value="email">
                            <T context="send statement preview tab">
                              Email Body
                            </T>
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent
                          value="email"
                          className="min-h-0 flex-1 overflow-auto"
                        >
                          <HtmlWYSIWYGEditor
                            key={currentCustomer.customerId}
                            initialHtml={
                              bodyDraft || displayPreview.htmlContent
                            }
                            onChange={handleBodyChange}
                            height={360}
                            templateVariables={displayPreview.templateVariables}
                          />
                          <p className="mt-2 text-xs text-muted-foreground">
                            <T context="send statement email body editor helper text">
                              Highlighted values (e.g. totals, counts) update
                              automatically when you change the invoice list.
                              Your email signature (from Settings → User → Email
                              signature) is included below — edit it here to
                              tweak it for this send.
                            </T>
                          </p>
                        </TabsContent>
                        <TabsContent
                          value="invoices"
                          className="min-h-0 flex-1 flex flex-col rounded border"
                        >
                          <div className="px-3 py-2 border-b bg-background text-xs text-muted-foreground shrink-0">
                            <T context="send statement invoice list count">
                              <Var>
                                {allInvoices.length - excludedInvoiceIds.size}
                              </Var>{' '}
                              of <Var>{allInvoices.length}</Var> invoices
                              included
                            </T>
                            {excludedInvoiceIds.size > 0 && (
                              <button
                                type="button"
                                className="ml-2 text-primary hover:underline"
                                onClick={() => {
                                  trackStatement(
                                    statementSteps.invoiceExclusionToggled,
                                    {
                                      customerId: currentCustomer?.customerId,
                                      action: 'include_all',
                                      clearedCount: excludedInvoiceIds.size,
                                    },
                                  );
                                  setExcludedInvoiceIds(new Set());
                                }}
                              >
                                <T context="button label, include all invoices in statement">
                                  Include all
                                </T>
                              </button>
                            )}
                            {invoiceHighlights.size > 0 && (
                              <button
                                type="button"
                                className="ml-2 text-primary hover:underline"
                                onClick={() => {
                                  trackStatement(
                                    statementSteps.highlightsCleared,
                                    {
                                      customerId: currentCustomer?.customerId,
                                      count: invoiceHighlights.size,
                                    },
                                  );
                                  setInvoiceHighlights(new Map());
                                }}
                              >
                                <T context="button label, clear all invoice highlight colors">
                                  Clear highlights
                                </T>
                              </button>
                            )}
                          </div>
                          {allInvoices.length === 0 ? (
                            <div className="py-8 text-center text-sm text-muted-foreground">
                              <T context="send statement invoice list empty state">
                                No invoices match the report criteria
                              </T>
                            </div>
                          ) : (
                            <>
                              <table className="w-full text-sm shrink-0 table-fixed">
                                <colgroup>
                                  <col style={{ width: '44px' }} />
                                  <col style={{ width: '44px' }} />
                                  <col style={{ width: '12%' }} />
                                  <col style={{ width: '12%' }} />
                                  <col style={{ width: '11%' }} />
                                  <col style={{ width: '11%' }} />
                                  <col style={{ width: '7%' }} />
                                  <col style={{ width: '14%' }} />
                                  <col style={{ width: '13%' }} />
                                  <col />
                                </colgroup>
                                <thead>
                                  <tr className="border-b text-left text-xs text-muted-foreground">
                                    <th
                                      className="px-3 py-2"
                                      aria-label={gt('Include', {
                                        context:
                                          'table column header, include invoice checkbox',
                                      })}
                                    />
                                    <th
                                      className="px-3 py-2"
                                      title={gt(
                                        'Click a cell below to cycle through highlight colors',
                                        {
                                          context:
                                            'tooltip for invoice highlight column header',
                                        },
                                      )}
                                    >
                                      <HighlighterIcon
                                        className="h-4 w-4"
                                        aria-label={gt('Highlight', {
                                          context:
                                            'aria label for highlight icon button in invoice table header',
                                        })}
                                      />
                                    </th>
                                    <th className="px-3 py-2">
                                      <T context="table column header, invoice number">
                                        Invoice #
                                      </T>
                                    </th>
                                    <th className="px-3 py-2">
                                      <T context="table column header, customer purchase order number">
                                        PO #
                                      </T>
                                    </th>
                                    <th className="px-3 py-2">
                                      <T context="table column header">
                                        Due Date
                                      </T>
                                    </th>
                                    <th className="px-3 py-2">
                                      <T context="table column header">
                                        Ship Date
                                      </T>
                                    </th>
                                    <th className="px-3 py-2 text-right">
                                      <T context="table column header, days since due/ship">
                                        Days Out
                                      </T>
                                    </th>
                                    <th className="px-3 py-2 text-right">
                                      <T context="table column header, invoice amount">
                                        Amount
                                      </T>
                                    </th>
                                    <th className="px-3 py-2 text-right">
                                      <T context="table column header, amount applied to invoice">
                                        Applied
                                      </T>
                                    </th>
                                    <th className="px-3 py-2 text-right">
                                      <T context="table column header, remaining invoice balance">
                                        Balance
                                      </T>
                                    </th>
                                  </tr>
                                </thead>
                              </table>
                              <div className="min-h-0 flex-1 overflow-auto">
                                <table className="w-full text-sm table-fixed">
                                  <colgroup>
                                    <col style={{ width: '44px' }} />
                                    <col style={{ width: '44px' }} />
                                    <col style={{ width: '12%' }} />
                                    <col style={{ width: '12%' }} />
                                    <col style={{ width: '11%' }} />
                                    <col style={{ width: '11%' }} />
                                    <col style={{ width: '7%' }} />
                                    <col style={{ width: '14%' }} />
                                    <col style={{ width: '13%' }} />
                                    <col />
                                  </colgroup>
                                  <tbody>
                                    {allInvoices.map((invoice) => {
                                      const included = !excludedInvoiceIds.has(
                                        invoice.id,
                                      );
                                      const highlightColor =
                                        invoiceHighlights.get(invoice.id);
                                      return (
                                        <tr
                                          key={invoice.id}
                                          className={`border-b hover:bg-muted/50 ${!included ? 'opacity-50' : ''}`}
                                        >
                                          <td className="px-3 py-2">
                                            <Checkbox
                                              checked={included}
                                              onCheckedChange={() => {
                                                // Currently included → this click
                                                // excludes it, and vice versa.
                                                sessionEngagedRef.current = true;
                                                trackStatement(
                                                  statementSteps.invoiceExclusionToggled,
                                                  {
                                                    customerId:
                                                      currentCustomer?.customerId,
                                                    invoiceId: invoice.id,
                                                    excluded: included,
                                                  },
                                                );
                                                setExcludedInvoiceIds(
                                                  (prev) => {
                                                    const next = new Set(prev);
                                                    if (next.has(invoice.id)) {
                                                      next.delete(invoice.id);
                                                    } else {
                                                      next.add(invoice.id);
                                                    }
                                                    return next;
                                                  },
                                                );
                                              }}
                                            />
                                          </td>
                                          <td className="px-3 py-2">
                                            <button
                                              type="button"
                                              onClick={() =>
                                                cycleHighlight(invoice.id)
                                              }
                                              className="h-4 w-4 rounded border border-gray-300 hover:border-gray-500 transition-colors"
                                              style={{
                                                backgroundColor: highlightColor
                                                  ? HIGHLIGHT_BG[highlightColor]
                                                  : 'transparent',
                                              }}
                                              aria-label={
                                                highlightColor
                                                  ? gt(
                                                      'Highlight: {color} (click to cycle)',
                                                      {
                                                        context:
                                                          'invoice row highlight button',
                                                        color: highlightColor,
                                                      },
                                                    )
                                                  : gt(
                                                      'No highlight (click to add)',
                                                      {
                                                        context:
                                                          'invoice row highlight button, no color set',
                                                      },
                                                    )
                                              }
                                              title={
                                                highlightColor
                                                  ? gt(
                                                      'Highlight: {color} — click to cycle',
                                                      {
                                                        context:
                                                          'tooltip for invoice row highlight button',
                                                        color: highlightColor,
                                                      },
                                                    )
                                                  : gt('Click to highlight', {
                                                      context:
                                                        'tooltip for invoice row highlight button, no color set',
                                                    })
                                              }
                                            />
                                          </td>
                                          <td className="px-3 py-2 font-medium">
                                            {invoice.invoiceNumber ?? '—'}
                                          </td>
                                          <td className="px-3 py-2 text-muted-foreground">
                                            {invoice.customerPurchaseOrderReference ??
                                              '—'}
                                          </td>
                                          <td className="px-3 py-2 text-muted-foreground">
                                            {formatDate(invoice.dueDate)}
                                          </td>
                                          <td className="px-3 py-2 text-muted-foreground">
                                            {formatDate(invoice.shipDate)}
                                          </td>
                                          <td className="px-3 py-2 text-right text-muted-foreground">
                                            {(() => {
                                              const basisDate =
                                                effectiveReportConfig.basis ===
                                                'days_overdue'
                                                  ? invoice.dueDate
                                                  : effectiveReportConfig.basis ===
                                                      'days_since_shipment'
                                                    ? (invoice.shipDate ??
                                                      invoice.invoiceDate)
                                                    : invoice.invoiceDate;
                                              const parsed =
                                                basisDate != null
                                                  ? parseDateOnly(basisDate)
                                                  : null;
                                              return parsed != null
                                                ? differenceInDays(
                                                    new Date(),
                                                    parsed,
                                                  )
                                                : '—';
                                            })()}
                                          </td>
                                          <td className="px-3 py-2 text-right font-semibold">
                                            {formatDecimalCurrency(
                                              invoice.netAmount,
                                            )}
                                          </td>
                                          <td className="px-3 py-2 text-right font-semibold">
                                            {invoice.netAmount != null &&
                                            invoice.balanceDue != null
                                              ? formatDecimalCurrency(
                                                  String(
                                                    parseFloat(
                                                      invoice.netAmount,
                                                    ) -
                                                      parseFloat(
                                                        invoice.balanceDue,
                                                      ),
                                                  ),
                                                )
                                              : '—'}
                                          </td>
                                          <td className="px-3 py-2 text-right font-semibold">
                                            {formatDecimalCurrency(
                                              invoice.balanceDue,
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </>
                          )}
                        </TabsContent>
                      </Tabs>
                    </>
                  ) : null}
                </div>

                {hasNoMatchingInvoices && (
                  <div className="flex items-center gap-2 border-t px-1 py-2 text-sm text-destructive">
                    <WarningIcon className="h-4 w-4" />
                    <T context="send statement dialog warning when no invoices match the report criteria">
                      No invoices match the report criteria — adjust the filters
                      on the Invoices tab before sending.
                    </T>
                  </div>
                )}

                <DialogFooter className="flex justify-between sm:justify-between">
                  {currentCustomerStatus === 'canceled' ? (
                    <div className="flex w-full items-center justify-between gap-2">
                      <span className="flex items-center gap-2 text-sm text-muted-foreground">
                        <ProhibitIcon className="h-4 w-4" />
                        <T context="review footer notice shown when viewing a customer that was canceled from the batch">
                          Canceled — this customer won’t be sent a statement.
                        </T>
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => readdToBatch(currentCustomer.customerId)}
                        disabled={isSendingAll}
                      >
                        <T context="button label, re-add a canceled customer back into the bulk send batch">
                          Re-add to batch
                        </T>
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        {!isSingle && (
                          <Button
                            variant="outline"
                            onClick={goBack}
                            disabled={!canGoBack || isSendingAll}
                          >
                            <T context="button label, go back to previous customer in bulk send">
                              Back
                            </T>
                          </Button>
                        )}
                        {!isSingle && (
                          <Button
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isSendingAll}
                          >
                            <T context="button label, explicitly mark this customer as do-not-send in bulk flow">
                              Cancel Send
                            </T>
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={
                            isSingle
                              ? () => handleOpenChange(false)
                              : handleSkip
                          }
                          disabled={isSendingAll}
                        >
                          {isSingle
                            ? gt('Cancel', {
                                context:
                                  'button label, cancel send statement dialog',
                              })
                            : gt('Skip', {
                                context:
                                  'button label, skip this customer in bulk send',
                              })}
                        </Button>
                      </div>
                      <div className="flex items-center gap-2">
                        {!isSingle && (
                          <Button
                            variant="outline"
                            onClick={handleSendAllClick}
                            disabled={isSendingAll || arModuleConfigPending}
                          >
                            {isPreparingSendAll ? (
                              <>
                                <SpinnerIcon className="mr-1.5 h-4 w-4 animate-spin" />
                                <T context="button label, refreshing invoice data before bulk send">
                                  Refreshing invoice data...
                                </T>
                              </>
                            ) : isSendingAll ? (
                              <>
                                <SpinnerIcon className="mr-1.5 h-4 w-4 animate-spin" />
                                <T context="button label, bulk send in progress">
                                  Sending All...
                                </T>
                              </>
                            ) : (
                              <T context="button label, send all remaining customer statements">
                                Send All
                              </T>
                            )}
                          </Button>
                        )}
                        <Button
                          onClick={() => setShowConfirm(true)}
                          disabled={
                            !hasRecipients ||
                            isSendingAll ||
                            !isPreviewCurrent ||
                            hasNoMatchingInvoices
                          }
                        >
                          <EnvelopeIcon className="mr-1.5 h-4 w-4" />
                          <T context="button label, send AR statement email">
                            Send Statement
                          </T>
                        </Button>
                      </div>
                    </>
                  )}
                </DialogFooter>
              </>
            )}

            {/* ── Summary Phase ─────────────────────────────────────── */}
            {phase === 'summary' && (
              <>
                <div className="min-h-0 flex-1 flex flex-col gap-4 overflow-auto py-2">
                  <div className="flex items-center gap-4 text-sm">
                    {sentCount > 0 && (
                      <span className="flex items-center gap-1 text-green-700">
                        <CheckCircleIcon className="h-4 w-4" weight="fill" />
                        <T context="bulk send summary count">
                          <Var>{sentCount}</Var> sent
                        </T>
                      </span>
                    )}
                    {skippedCount > 0 && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <WarningCircleIcon className="h-4 w-4" />
                        <T context="bulk send summary count">
                          <Var>{skippedCount}</Var> skipped
                        </T>
                      </span>
                    )}
                    {canceledCount > 0 && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <ProhibitIcon className="h-4 w-4" />
                        <T context="bulk send summary count">
                          <Var>{canceledCount}</Var> canceled
                        </T>
                      </span>
                    )}
                    {failedCount > 0 && (
                      <span className="flex items-center gap-1 text-red-700">
                        <XCircleIcon className="h-4 w-4" weight="fill" />
                        <T context="bulk send summary count">
                          <Var>{failedCount}</Var> failed
                        </T>
                      </span>
                    )}
                    {pendingCount > 0 && (
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <ClockIcon className="h-4 w-4" />
                        <T context="bulk send summary count">
                          <Var>{pendingCount}</Var> pending
                        </T>
                      </span>
                    )}
                  </div>

                  <div className="space-y-1">
                    {customers.map((customer, customerIndex) => {
                      const result = results.get(customer.customerId);
                      // Un-actioned customers are listed too (dashed, muted) so
                      // a summary opened mid-flow accounts for the whole list
                      // rather than silently omitting the work that's left.
                      if (!result) {
                        return (
                          <button
                            key={customer.customerId}
                            type="button"
                            onClick={() => viewCustomer(customerIndex)}
                            className={cn(
                              'flex w-full items-center justify-between gap-3 rounded-md border border-dashed px-3 py-2 text-left text-sm text-muted-foreground',
                              'hover:bg-muted cursor-pointer',
                            )}
                          >
                            <span className="truncate">
                              {customer.customerName ??
                                gt('(Unknown)', {
                                  context: 'unknown customer name placeholder',
                                })}
                            </span>
                            <span className="shrink-0 text-xs">
                              <T context="bulk send result status for a customer that has not been reviewed yet">
                                Not sent yet · click to review
                              </T>
                            </span>
                          </button>
                        );
                      }
                      const canRevisit =
                        result.status === 'skipped' ||
                        result.status === 'failed' ||
                        result.status === 'canceled';
                      // Sent rows open the sent-email viewer to confirm what
                      // went out (only once the outbound email id is known).
                      const canViewSent =
                        result.status === 'sent' &&
                        result.outboundEmailId != null;
                      const customerId = customer.customerId;
                      const rowClickable =
                        (canRevisit && customerIndex >= 0) || canViewSent;
                      const rowClassName = cn(
                        'flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm',
                        rowClickable && 'hover:bg-muted cursor-pointer',
                      );
                      const rowBody = (
                        <>
                          <span className="font-medium truncate">
                            {result.customerName ??
                              gt('(Unknown)', {
                                context: 'unknown customer name placeholder',
                              })}
                          </span>
                          <div className="flex items-center gap-3 shrink-0">
                            <span
                              className={
                                result.status === 'sent'
                                  ? 'text-green-700'
                                  : result.status === 'failed'
                                    ? 'text-red-700'
                                    : 'text-muted-foreground'
                              }
                            >
                              {result.status === 'sent' && (
                                <T context="bulk send result status">Sent</T>
                              )}
                              {result.status === 'skipped' && (
                                <T context="bulk send result status">Skipped</T>
                              )}
                              {result.status === 'canceled' && (
                                <T context="bulk send result status">
                                  Canceled
                                </T>
                              )}
                              {result.status === 'failed' && (
                                <T context="bulk send result status, with error detail">
                                  Failed:{' '}
                                  <Var>
                                    {result.error ??
                                      gt('Unknown error', {
                                        context: 'fallback error message',
                                      })}
                                  </Var>
                                </T>
                              )}
                            </span>
                            {canViewSent ? (
                              <span className="text-xs text-muted-foreground">
                                <T context="hint shown next to a sent summary row; clicking opens the sent email">
                                  Click to view
                                </T>
                              </span>
                            ) : canRevisit ? (
                              <span className="text-xs text-muted-foreground">
                                <T context="hint shown next to revisitable summary rows">
                                  Click to revisit
                                </T>
                              </span>
                            ) : null}
                          </div>
                        </>
                      );
                      return rowClickable ? (
                        <button
                          key={customerId}
                          type="button"
                          onClick={() => {
                            if (canViewSent) {
                              // Enter the in-dialog read-only "sent" review for
                              // this customer (keeps the sent result intact).
                              viewCustomer(customerIndex);
                            } else if (canRevisit) {
                              revisitCustomer(customerIndex);
                            }
                          }}
                          className={rowClassName}
                        >
                          {rowBody}
                        </button>
                      ) : (
                        <div key={customerId} className={rowClassName}>
                          {rowBody}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Footer keeps its default right alignment so the report
                    actions sit directly beside "Done" rather than being pushed
                    to the opposite end of a very wide dialog, where users miss
                    them entirely and re-derive the summary by hand. */}
                <DialogFooter>
                  {/* Mid-flow detour: put the user back on the customer they
                      left (or the next untouched one) so the summary is never a
                      dead end they have to close the dialog to escape. */}
                  {resumeIndex !== null && (
                    <Button
                      variant="secondary"
                      onClick={() => viewCustomer(resumeIndex)}
                    >
                      <T context="button label, leave the bulk send summary and carry on reviewing statements">
                        Continue sending
                      </T>
                    </Button>
                  )}
                  {/* Report of who received a statement and the oldest invoice
                      on it. Hidden when nothing was sent — there'd be no rows. */}
                  {sentSummaries.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => void handleCopySummary()}
                      >
                        <T context="button label, copy the bulk send summary report to the clipboard">
                          Copy summary
                        </T>
                      </Button>
                      <Button variant="outline" onClick={handleDownloadSummary}>
                        <T context="button label, download the bulk send summary report as a CSV file">
                          Download CSV
                        </T>
                      </Button>
                    </>
                  )}
                  <Button onClick={() => handleOpenChange(false)}>
                    <T context="button label, close bulk send summary dialog">
                      Done
                    </T>
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>

          {/* ── PDF preview column ──────────────────────────────── */}
          {phase === 'review' && currentCustomerStatus !== 'sent' && (
            <aside className="hidden xl:flex w-[620px] shrink-0 flex-col border-l pl-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium">
                <T context="pdf preview column heading">PDF preview</T>
                <span className="text-xs font-normal text-muted-foreground">
                  <T context="pdf preview column subheading, indicates the iframe reflects current invoice selections">
                    reflects invoice selections
                  </T>
                </span>
              </div>
              {displayPreview ? (
                <iframe
                  title={gt('PDF attachment preview', {
                    context: 'iframe title for PDF preview',
                  })}
                  src={`data:application/pdf;base64,${displayPreview.pdfBase64}`}
                  className="min-h-0 flex-1 w-full rounded border bg-muted/30"
                />
              ) : (
                <div className="min-h-0 flex-1 flex items-center justify-center rounded border bg-muted/30 text-xs text-muted-foreground">
                  {isPreviewing ? (
                    <span className="inline-flex items-center gap-2">
                      <SpinnerIcon className="h-4 w-4 animate-spin" />
                      <T context="pdf preview loading state">
                        Loading preview...
                      </T>
                    </span>
                  ) : previewError ? (
                    <T context="pdf preview error state">
                      Failed to load preview
                    </T>
                  ) : null}
                </div>
              )}
            </aside>
          )}
        </div>
      </DialogContent>

      {/* ── Confirm dialog ──────────────────────────────────── */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              <T context="confirm send statement dialog title">Confirm Send</T>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1 text-sm">
            <div className="flex gap-2">
              <span className="w-12 shrink-0 font-medium text-muted-foreground">
                <T context="email header From field">From:</T>
              </span>
              <span className="text-foreground">
                {sendFromOptions.find((o) => o.key === selectedSendFromKey)
                  ?.label ?? displayPreview?.from}
              </span>
            </div>
            {toEmails.length > 0 && (
              <div className="flex gap-2">
                <span className="w-12 shrink-0 font-medium text-muted-foreground">
                  <T context="email header To field">To:</T>
                </span>
                <span className="text-foreground">{toEmails.join(', ')}</span>
              </div>
            )}
            {ccEmails.length > 0 && (
              <div className="flex gap-2">
                <span className="w-12 shrink-0 font-medium text-muted-foreground">
                  <T context="email header CC field">CC:</T>
                </span>
                <span className="text-foreground">{ccEmails.join(', ')}</span>
              </div>
            )}
            {(bccEmails.length > 0 ||
              (bccSender && currentUser?.emails[0])) && (
              <div className="flex gap-2">
                <span className="w-12 shrink-0 font-medium text-muted-foreground">
                  <T context="email header BCC field">BCC:</T>
                </span>
                <span className="text-foreground">
                  {[
                    ...(bccSender && currentUser?.emails[0]
                      ? [currentUser.emails[0]]
                      : []),
                    ...bccEmails,
                  ].join(', ')}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirm(false)}
              disabled={isSending}
            >
              <T context="button label, cancel send statement confirmation">
                Cancel
              </T>
            </Button>
            <Button
              onClick={handleConfirmedSend}
              disabled={isSending || !isPreviewCurrent || hasNoMatchingInvoices}
            >
              {isSending ? (
                <>
                  <SpinnerIcon className="mr-1.5 h-4 w-4 animate-spin" />
                  <T context="button label, sending statement in progress">
                    Sending...
                  </T>
                </>
              ) : (
                <T context="button label, confirm and send statement">
                  Confirm &amp; Send
                </T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Send All confirm ─────────────────────────────────────────── */}
      <Dialog open={showSendAllConfirm} onOpenChange={setShowSendAllConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              <T context="bulk send confirm dialog title">
                Send all statements?
              </T>
            </DialogTitle>
          </DialogHeader>
          {unvisitedCustomers.length > 0 ? (
            <p className="text-sm text-muted-foreground">
              <T context="bulk send confirm dialog body, warns about unreviewed customers">
                <span className="font-medium text-foreground">
                  <Var>{unvisitedCustomers.length}</Var> of{' '}
                  <Var>{remainingCount}</Var>
                </span>{' '}
                remaining{' '}
                <Plural
                  n={remainingCount}
                  one="customer hasn’t"
                  other="customers haven’t"
                />{' '}
                been reviewed and will be sent using their default settings
                (last saved recipients, default subject and body). Customers you
                reviewed will use the edits you made.
              </T>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              <T context="bulk send confirm dialog body, all customers reviewed">
                This will send statements to{' '}
                <span className="font-medium text-foreground">
                  <Var>{remainingCount}</Var>{' '}
                  <Plural n={remainingCount} one="customer" other="customers" />
                </span>{' '}
                using the edits you made. This action can’t be undone.
              </T>
            </p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSendAllConfirm(false)}
              disabled={isSendingAll}
            >
              <T context="button label, cancel bulk send confirm dialog">
                Cancel
              </T>
            </Button>
            <Button
              onClick={() => void handleSendAll()}
              disabled={isSendingAll}
            >
              <T context="button label, confirm bulk send of remaining statements">
                Send All
              </T>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
